import { useState, type FormEvent } from 'react';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { FormField } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import type { ShippingPromotion, ShippingPromotionFee, ShippingPromotionInput } from '../../lib/api/operatorConsole';
import { backendMessage } from '../TestPlayers/backendMessage';
import {
  browserTimeZone,
  draftFromPromotion,
  draftToInput,
  emptyDraft,
  FEE_OPTIONS,
  KIND_OPTIONS,
  pick,
  VALUE_FIELD,
  type PromotionDraft,
} from './shippingPromotions.helpers';

const FORM_ID = 'shipping-promotion-form';
const LABEL_MAX_LENGTH = 80;
const HINT = 'text-[11px] text-[var(--co-muted)]';

interface Props {
  /** The promotion being edited; null starts a new one. */
  promotion: ShippingPromotion | null;
  currency: string;
  /** Rejects with the backend's error, which the form shows and stays open on. */
  onSubmit: (input: ShippingPromotionInput) => Promise<void>;
  onClose: () => void;
}

export function PromotionForm({ promotion, currency, onSubmit, onClose }: Props) {
  const [draft, setDraft] = useState<PromotionDraft>(() =>
    promotion ? draftFromPromotion(promotion) : emptyDraft(),
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const timeZone = browserTimeZone();
  const usesPercent = draft.kind === 'percent_off';
  const valueField = VALUE_FIELD[draft.kind];

  function update<K extends keyof PromotionDraft>(key: K, value: PromotionDraft[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function toggleFee(fee: ShippingPromotionFee, covered: boolean) {
    update('covers', covered ? [...draft.covers, fee] : draft.covers.filter((other) => other !== fee));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;

    const result = draftToInput(draft);
    if (!result.ok) {
      setError(result.error);
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(result.input);
    } catch (err) {
      setError(backendMessage(err) ?? 'Could not save this promotion.');
      setSubmitting(false);
    }
  }

  const submitLabel = promotion ? 'Save changes' : 'Create promotion';

  return (
    <Modal
      open
      title={promotion ? 'Edit promotion' : 'New promotion'}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" form={FORM_ID} disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
        </div>
      }
    >
      <form id={FORM_ID} noValidate onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-4">
        <FormField label="Label" htmlFor="promotion-label">
          <Input
            id="promotion-label"
            required
            value={draft.label}
            maxLength={LABEL_MAX_LENGTH}
            placeholder="November shipping promo"
            onChange={(e) => update('label', e.target.value)}
          />
          <p className={HINT}>Shown in this list and returned to your platform with the prices.</p>
        </FormField>

        <FormField label="Type" htmlFor="promotion-kind">
          <Select
            id="promotion-kind"
            aria-label="Type"
            value={draft.kind}
            options={KIND_OPTIONS}
            onChange={(value) => update('kind', pick(KIND_OPTIONS, value, draft.kind))}
          />
        </FormField>

        <FormField label={`${valueField.label} (${usesPercent ? '%' : currency})`} htmlFor="promotion-value">
          <Input
            id="promotion-value"
            required
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={usesPercent ? draft.percentOff : draft.amount}
            onChange={(e) => update(usesPercent ? 'percentOff' : 'amount', e.target.value)}
          />
          <p className={HINT}>{valueField.hint}</p>
        </FormField>

        <fieldset className="flex flex-col gap-1.5">
          <legend className="mb-1.5 text-sm font-medium text-[var(--co-text)]">Covers</legend>
          {FEE_OPTIONS.map((fee) => (
            <div key={fee.value} className="flex items-center gap-2">
              <div className="w-24 shrink-0">
                <Checkbox
                  aria-label={fee.label}
                  label={fee.label}
                  checked={draft.covers.includes(fee.value)}
                  onChange={(covered) => toggleFee(fee.value, covered)}
                />
              </div>
              <span className={HINT}>{fee.hint}</span>
            </div>
          ))}
          <p className={HINT}>Players pay anything it does not cover at the regular price, on top of the promotional price.</p>
        </fieldset>

        <FormField label={`Max discount (${currency}, optional)`} htmlFor="promotion-cap">
          <Input
            id="promotion-cap"
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={draft.maxDiscount}
            onChange={(e) => update('maxDiscount', e.target.value)}
          />
          <p className={HINT}>The most you fund on one delivery. Leave empty for no cap.</p>
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Starts" htmlFor="promotion-starts-at">
            <Input
              id="promotion-starts-at"
              required
              type="datetime-local"
              value={draft.startsAt}
              onChange={(e) => update('startsAt', e.target.value)}
            />
          </FormField>
          <FormField label="Ends" htmlFor="promotion-ends-at">
            <Input
              id="promotion-ends-at"
              required
              type="datetime-local"
              value={draft.endsAt}
              onChange={(e) => update('endsAt', e.target.value)}
            />
          </FormField>
        </div>
        <p className={`-mt-2 ${HINT}`}>Times are in your browser’s time zone: {timeZone}.</p>

        {error && (
          <p role="alert" className="text-[12px] text-[var(--co-error)]">
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}
